import { Injectable } from '@angular/core';

export interface UserProfile {
  phone: string;
  firstName: string;
  lastName: string;
  email: string;
  active: boolean;
}

export interface ClientInfo {
  _id: string;
  profile: UserProfile;
}

export interface UserData {
  user: {
    _id: string;
    username: string;
  };
}

export interface UserEmail {
  address: string;
  verified: boolean;
}

export interface UserProfileExtended {
  firstName: string;
  lastName: string;
  isNew: boolean;
  image_url: string;
}

export interface UserStripeInfo {
  customerId: string;
  status: string;
  subscriptionId: string;
  trialEnd: {
    $date: number;
  };
}

export interface UserDataMessage {
  msg: string;
  collection: string;
  id: string;
  fields: {
    emails: UserEmail[];
    profile: UserProfileExtended;
    username: string;
    userIds: string[];
    stripe: UserStripeInfo;
  };
}

export interface LoginResult {
  id: string;
  token: string;
  tokenExpires: {
    $date: number;
  };
  type: string;
}

export interface DDPMessage {
  msg: string;
  id?: string;
  method?: string;
  params?: any[];
  result?: any;
  error?: any;
  session?: string;
  collection?: string;
  fields?: any;
  version?: string;
  support?: string[];
}

export interface QuickCoachLoginState {
  step: 'email' | 'code' | 'success' | 'error';
  email?: string;
  userId?: string;
  error?: string;
  loginToken?: string;
  userData?: UserDataMessage;
}

export interface ClientNote {
  _id: string;
  coachId: string;
  clientId: string;
  content: string;
  createdAt: number;
}

export interface ClientInfoExtended {
  _id: string;
  profile: {
    phone: string;
    firstName: string;
    lastName: string;
    email: string;
    active: boolean;
  };
}

export interface ClientWithNotes extends ClientInfoExtended {
  notes: ClientNote[];
}

@Injectable({
  providedIn: 'root'
})
export class QuickcoachImportService {
  private readonly WEBSOCKET_URL = 'wss://app.quickcoach.fit/sockjs/831/gtcof2fh/websocket';
  private readonly STORAGE_KEY = 'quickcoach_login_token';

  // WebSocket properties
  private ws: WebSocket | null = null;
  private pendingRequests = new Map<string, { resolve: any; reject: any }>();
  private messageId = 0;
  private isConnected = false;
  private userDataResolvers: any[] = [];

  constructor() {}

  // WebSocket connection methods
  private connect(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(url);
      this.setupEventHandlers();

      const timeout = setTimeout(() => {
        reject(new Error('Connection timeout'));
      }, 10000);

      this.ws.onopen = () => {
        clearTimeout(timeout);
        console.log('WebSocket connection opened');
        this.isConnected = true;
        resolve();
      };

      this.ws.onerror = (error) => {
        clearTimeout(timeout);
        reject(error);
      };
    });
  }

  private setupEventHandlers(): void {
    if (!this.ws) return;

    this.ws.onmessage = (event) => {
      this.handleMessage(event.data);
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.pendingRequests.forEach((resolve) => {
        resolve.reject(error);
      });
      this.pendingRequests.clear();
    };

    this.ws.onclose = (event) => {
      console.log(`WebSocket closed: ${event.code} - ${event.reason}`);
      this.isConnected = false;
      this.pendingRequests.forEach((resolve) => {
        resolve.reject(new Error('WebSocket connection closed'));
      });
      this.pendingRequests.clear();
    };
  }

  private handleMessage(data: string): void {
    const rawMessage = data.toString();
    console.log('Received:', rawMessage);

    try {
      if (rawMessage === 'o') {
        console.log('SockJS open frame received');
        return;
      }

      if (rawMessage === 'h') {
        console.log('SockJS heartbeat received');
        return;
      }

      let messageArray: any[];

      if (rawMessage.startsWith('a[')) {
        const jsonPart = rawMessage.substring(1);
        messageArray = JSON.parse(jsonPart);
      } else if (rawMessage.startsWith('[')) {
        messageArray = JSON.parse(rawMessage);
      } else {
        console.log('Other SockJS frame:', rawMessage);
        return;
      }

      if (Array.isArray(messageArray) && messageArray.length > 0) {
        const innerMessage: DDPMessage = JSON.parse(messageArray[0]);
        console.log('Parsed DDP message:', JSON.stringify(innerMessage, null, 2));

        if (innerMessage.msg === 'result') {
          this.handleResult(innerMessage);
        } else if (innerMessage.msg === 'connected') {
          console.log('DDP CONNECTION ESTABLISHED');
          console.log('Session ID:', innerMessage.session);
        } else if (innerMessage.msg === 'added') {
          console.log(`DATA ADDED TO COLLECTION: ${innerMessage.collection}`);

          if (innerMessage.collection === 'users' && this.userDataResolvers.length > 0) {
            console.log('Resolving user data promises with:', innerMessage);
            this.userDataResolvers.forEach(resolve => resolve(innerMessage));
            this.userDataResolvers = [];
          }
        }
      }
    } catch (error) {
      console.error('Error parsing message:', error);
      console.error('Raw message was:', rawMessage);
    }
  }

  private handleResult(innerMessage: DDPMessage): void {
    const requestId = innerMessage.id!;
    const pendingRequest = this.pendingRequests.get(requestId);

    if (pendingRequest) {
      if (innerMessage.error) {
        pendingRequest.reject(innerMessage);
      } else {
        pendingRequest.resolve(innerMessage.result);
      }
      this.pendingRequests.delete(requestId);
    } else {
      console.log(`UNHANDLED RESULT (ID: ${requestId})`);
      if (innerMessage.error) {
        console.log('Error:', JSON.stringify(innerMessage.error, null, 2));
      } else {
        console.log(JSON.stringify(innerMessage.result, null, 2));
      }
    }
  }

  private sendMessage(message: DDPMessage): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.isConnected || !this.ws) {
        reject(new Error('WebSocket is not connected'));
        return;
      }

      if (message.msg === 'method') {
        this.messageId++;
        const requestId = this.messageId.toString();
        message.id = requestId;

        this.pendingRequests.set(requestId, { resolve, reject });

        setTimeout(() => {
          if (this.pendingRequests.has(requestId)) {
            this.pendingRequests.delete(requestId);
            reject(new Error(`Request timeout for ID: ${requestId}`));
          }
        }, 30000);
      }

      const payload = JSON.stringify([JSON.stringify(message)]);
      this.ws.send(payload);

      if (message.msg !== 'method') {
        resolve(null);
      }
    });
  }

  private async initializeWebSocketConnection(): Promise<void> {
    await this.sendMessage({
      msg: "connect",
      version: "1",
      support: ["1", "pre2", "pre1"]
    });
  }

  private async getUserByEmail(email: string): Promise<UserData> {
    return this.sendMessage({
      msg: "method",
      method: "accounts.check.email",
      params: [{ email }]
    });
  }

  // Make getUserByEmail available publicly
  async getUserByEmailPublic(email: string): Promise<UserData> {
    return this.getUserByEmail(email);
  }

  private async getAuthenticationCode(userId: string): Promise<any> {
    const sha256 = "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855";

    return this.sendMessage({
      msg: "method",
      method: "twoFactor.getAuthenticationCode",
      params: [
        { id: userId },
        {
          digest: sha256,
          algorithm: "sha-256"
        }
      ]
    });
  }

  private async sendLoginCode(userId: string, email: string): Promise<any> {
    return this.sendMessage({
      msg: "method",
      method: "sendLogInCode",
      params: [{
        userId,
        email
      }]
    });
  }

  private async verifyCodeAndLogin(userId: string, code: string): Promise<LoginResult> {
    const sha256 = "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855";

    return this.sendMessage({
      msg: "method",
      method: "twoFactor.verifyCodeAndLogin",
      params: [
        {
          user: { id: userId },
          password: {
            digest: sha256,
            algorithm: "sha-256"
          },
          code
        }
      ]
    });
  }

  private async loginWithToken(token: string): Promise<LoginResult> {
    return this.sendMessage({
      msg: "method",
      method: "login",
      params: [{
        resume: token
      }]
    });
  }

  private waitForUserDataInternal(timeout = 10000): Promise<UserDataMessage> {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        const index = this.userDataResolvers.indexOf(wrappedResolve);
        if (index > -1) {
          this.userDataResolvers.splice(index, 1);
        }
        reject(new Error('Timeout waiting for user data'));
      }, timeout);

      const wrappedResolve = (userData: UserDataMessage) => {
        clearTimeout(timeoutId);
        resolve(userData);
      };

      this.userDataResolvers.push(wrappedResolve);
    });
  }

  // LocalStorage utility methods
  private saveLoginToken(token: string): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, token);
    } catch (error) {
      console.error('Failed to save login token to localStorage:', error);
    }
  }

  private getStoredLoginToken(): string | null {
    try {
      return localStorage.getItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to retrieve login token from localStorage:', error);
      return null;
    }
  }

  private clearStoredLoginToken(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear login token from localStorage:', error);
    }
  }

  // Public API methods
  async initializeConnection(): Promise<void> {
    await this.connect(this.WEBSOCKET_URL);
    await this.initializeWebSocketConnection();
  }

  async tryLoginWithStoredToken(): Promise<LoginResult | null> {
    const storedToken = this.getStoredLoginToken();

    if (!storedToken) {
      return null;
    }

    try {
      const loginResult = await this.loginWithExistingToken(storedToken);
      return loginResult;
    } catch (error) {
      console.error('Failed to login with stored token:', error);
      // Clear invalid token
      this.clearStoredLoginToken();
      return null;
    }
  }

  async startLoginFlow(email: string): Promise<{ userId: string }> {
    try {
      // Check if user exists
      const userInfo: UserData = await this.getUserByEmail(email);

      if (!userInfo.user) {
        throw new Error('User not found');
      }

      const userId = userInfo.user._id;

      // Get authentication code
      await this.getAuthenticationCode(userId);

      // Send login code email
      await this.sendLoginCode(userId, email);

      return { userId };
    } catch (error) {
      console.error('Error starting login flow:', error);
      throw error;
    }
  }

  async verifyLoginCode(userId: string, code: string): Promise<LoginResult> {
    try {
      const loginResult = await this.verifyCodeAndLogin(userId, code);

      // Persist the login token to localStorage
      this.saveLoginToken(loginResult.token);

      return loginResult;
    } catch (error) {
      console.error('Error verifying login code:', error);
      throw error;
    }
  }

  async loginWithExistingToken(token: string): Promise<LoginResult> {
    try {
      const loginResult = await this.loginWithToken(token);
      return loginResult;
    } catch (error) {
      console.error('Error logging in with existing token:', error);
      throw error;
    }
  }

  async waitForUserData(): Promise<UserDataMessage> {
    return this.waitForUserDataInternal();
  }

  disconnect(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
    }
  }

  logout(): void {
    this.disconnect();
    this.clearStoredLoginToken();
  }

  get connected(): boolean {
    return this.isConnected;
  }

  // Helper method to get client notes
  private async getClientNotes(clientId: string): Promise<ClientNote[]> {
    const customerNotes = await this.sendMessage({
      msg: "method",
      method: "named_query_getCoachClientsNotes",
      params: [
        {
          filters: {
            clientId
          },
          options: {
            skip: 0,
            sort: {
              pinned: -1,
              createdAt: -1
            }
          }
        }
      ]
    });

    return customerNotes || [];
  }

  // Helper method to get client info
  private async getClientInfo(clientId: string): Promise<ClientInfoExtended> {
    const clientsInfo = await this.sendMessage({
      msg: "method",
      method: "named_query_getCustomerDetailsClientActionPage",
      params: [
        {
          filters: {
            _id: clientId
          }
        }
      ]
    });

    return clientsInfo[0];
  }

  // Public method to get customers with notes
  async getCustomers(userIds: string[]): Promise<ClientWithNotes[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to QuickCoach API');
    }

    // Get all client info in parallel
    const clients = await Promise.all(userIds.map(userId => this.getClientInfo(userId)));

    // Get notes for all clients in parallel
    const allClientNotes = await Promise.all(userIds.map(userId => this.getClientNotes(userId)));
    const clientNotes = allClientNotes.reduce((acc, notes) => acc.concat(notes), []);

    // Combine clients with their notes
    const clientsWithNotes = clients.map(client => {
      const matchingNotes = clientNotes.filter(note => note.clientId === client._id);

      return {
        ...client,
        notes: matchingNotes
      };
    });

    return clientsWithNotes;
  }
}
